import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { api, BACKEND } from "@/lib/api";
import { Spinner } from "@/components/common";
import { Conversions } from "@/components/admin/Conversions";

let first = true;

// Cookie-free page-view beacon: path, where the visit came from (first page only) and the browser's time zone (for country).
export const VisitTracker = () => {
  const { pathname, search } = useLocation();
  useEffect(() => {
    const q = new URLSearchParams(search);
    const referrer = !first ? "" : q.get("from") ? `https://${q.get("from")}/` : document.referrer;
    const body = { path: pathname, referrer, first, via: first ? (q.get("via") || "").slice(0, 20) : "", shared: first && (q.has("ref") || q.has("via")), tz: Intl.DateTimeFormat().resolvedOptions().timeZone || "" };
    first = false;
    fetch(`${BACKEND}/api/public/visit`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body), keepalive: true }).catch(() => {});
  }, [pathname]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
};

const countryName = (cc) => { try { return cc ? new Intl.DisplayNames(undefined, { type: "region" }).of(cc) : "Unknown"; } catch { return cc; } };

const Bars = ({ title, rows, name = (x) => x, testid }) => {
  const max = Math.max(1, ...rows.map((r) => r.visits));
  return (
    <div data-testid={testid}><h3 className="mb-2 text-lg">{title}</h3>
      {!rows.length ? <p className="text-sm text-slate-400">No data yet.</p> : <div className="space-y-1.5">{rows.map((r) => (
        <div key={r.name} className="flex items-center gap-3 text-sm" data-testid={`${testid}-${r.name}`}>
          <span className="w-36 shrink-0 truncate" title={name(r.name)}>{name(r.name)}</span>
          <span className="h-2 flex-1 rounded-full bg-slate-100"><span className="block h-2 rounded-full bg-[#1B2232]" style={{ width: `${(r.visits / max) * 100}%` }} /></span>
          <span className="w-12 text-right tabular-nums text-slate-600">{r.visits}</span>
        </div>))}</div>}
    </div>
  );
};

const RANGES = [[7, "7 days"], [30, "30 days"], [90, "90 days"]];

export const Visitors = () => {
  const [days, setDays] = useState(30);
  const [d, setD] = useState(null);
  useEffect(() => { setD(null); api.get("/admin/analytics/visitors", { params: { days } }).then((r) => setD(r.data)).catch(() => setD({ error: true })); }, [days]);
  return (
    <section className="surface space-y-5 p-5" data-testid="analytics-visitors">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div><h2 className="text-2xl">Visitors</h2><p className="mt-1 text-sm text-slate-500">Counted without cookies or personal data. A visitor is counted once per day; people can't be followed across days.</p></div>
        <div className="flex gap-1.5">{RANGES.map(([n, l]) => <button key={n} onClick={() => setDays(n)} className={`chip !py-1 text-xs ${days === n ? "chip-active" : ""}`} data-testid={`visitors-range-${n}`}>{l}</button>)}</div>
      </div>
      {!d ? <Spinner /> : d.error ? <p className="text-sm text-rose-600">Couldn't load visitor stats.</p> : (<>
        <div className="grid grid-cols-3 gap-3">
          {[["Daily visitors (summed)", d.totals.visitors, "visitors-total"], ["Visits", d.totals.visits, "visitors-visits"], ["Page views", d.totals.views, "visitors-views"]].map(([l, v, t]) => (
            <div key={t} className="rounded-xl bg-[#F5F1EB] p-4" data-testid={t}><p className="text-xs text-slate-500">{l}</p><p className="text-3xl font-semibold">{v}</p></div>))}
        </div>
        <div><h3 className="text-lg">Visitors and page views per day</h3>
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={d.daily} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
              <CartesianGrid stroke="#e2e8f0" vertical={false} />
              <XAxis dataKey="day" tickFormatter={(x) => x.slice(5)} minTickGap={20} tick={{ fontSize: 11, fill: "#64748b" }} /><YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#64748b" }} />
              <Tooltip /><Area dataKey="views" name="Page views" stroke="#94a3b8" fill="#e2e8f0" isAnimationActive={false} /><Area dataKey="visitors" name="Visitors" stroke="#1B2232" fill="#1B2232" fillOpacity={0.15} isAnimationActive={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="grid gap-6 md:grid-cols-2">
          <Bars title="Where visits came from" rows={d.sources} testid="visitors-sources" />
          <Bars title="Visitor countries" rows={d.countries} name={countryName} testid="visitors-countries" />
          <Bars title="Most viewed pages" rows={d.pages} testid="visitors-pages" />
          <Bars title="Shared links opened" rows={d.shared_opens} name={(k) => ({ fact: "Facts", live: "Live issues", perspective: "Perspectives", claim: "Fact checks", card: "Share cards" }[k] || k)} testid="visitors-shared" />
        </div>
        <Conversions rows={d.conversions} />
        <p className="text-xs text-slate-400">Your own visits and other admins' are left out: once an admin signs in, that device isn't counted for the rest of the day. Country comes from the visitor's device time zone. Some apps, like WhatsApp, often don't say where a visit came from, so those visits show as "Direct or unknown" unless the link was shared from Middle.</p>
      </>)}
    </section>
  );
};
